// =====================================================
// TREATMENT GROUPS API CLIENT
// Rutas verificadas contra treatment-groups.controller.ts:
//   GET    /treatment-groups                → findAll (lista simple, no paginada)
//   GET    /treatment-groups/paginated      → findAllPaginated ← ruta correcta
//   GET    /treatment-groups/select         → findForSelect
//   GET    /treatment-groups/export         → exportToExcel
//   GET    /treatment-groups/template       → getTemplate
//   POST   /treatment-groups/import/preview → previewImport
//   POST   /treatment-groups/import         → importFromExcel
//   GET    /treatment-groups/:id            → findOne
//   POST   /treatment-groups                → create
//   PATCH  /treatment-groups/:id            → update
//   DELETE /treatment-groups/:id            → deactivate  ← DELETE, no PATCH
//   PATCH  /treatment-groups/:id/reactivate → reactivate
// =====================================================
import { api } from './client';
import type { PaginatedResponse } from '../types/pagination';

// ── Tipos ─────────────────────────────────────────────

export interface TreatmentGroupSelect {
  id: string;
  code: string;
  name: string;
}

export interface TreatmentGroup {
  id: string;
  companyId: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  medicalActsCount?: number;
  linkedSitesCount?: number;
}

export interface TreatmentGroupQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'name' | 'code' | 'sortOrder' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  includeInactive?: boolean;
}

export interface CreateTreatmentGroupRequest {
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
}

export interface UpdateTreatmentGroupRequest {
  code?: string;
  name?: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
}

// ── Cliente API ───────────────────────────────────────

export const treatmentGroupsApi = {
  /**
   * GET /treatment-groups/select
   * Lista para selects (solo activos, sin paginación)
   */
  getForSelect: async (): Promise<TreatmentGroupSelect[]> => {
    return api.get<TreatmentGroupSelect[]>('/treatment-groups/select');
  },

  /**
   * GET /treatment-groups/paginated?page=&limit=&search=&sortBy=&sortOrder=&includeInactive=
   * El controller tiene una ruta dedicada /paginated, distinta de GET /treatment-groups.
   */
  getAllPaginated: async (
    params?: TreatmentGroupQueryParams,
  ): Promise<PaginatedResponse<TreatmentGroup>> => {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) qs.append(k, String(v));
      });
    }
    const query = qs.toString();
    return api.get<PaginatedResponse<TreatmentGroup>>(
      query ? `/treatment-groups/paginated?${query}` : '/treatment-groups/paginated',
    );
  },

  /**
   * GET /treatment-groups?includeInactive=
   * Lista simple sin paginación (legado / uso interno)
   */
  getAll: async (includeInactive = false): Promise<TreatmentGroup[]> => {
    const query = includeInactive ? '?includeInactive=true' : '';
    return api.get<TreatmentGroup[]>(`/treatment-groups${query}`);
  },

  /**
   * GET /treatment-groups/:id
   */
  getById: async (id: string): Promise<TreatmentGroup> => {
    return api.get<TreatmentGroup>(`/treatment-groups/${id}`);
  },

  /**
   * POST /treatment-groups
   */
  create: async (data: CreateTreatmentGroupRequest): Promise<TreatmentGroup> => {
    return api.post<TreatmentGroup>('/treatment-groups', data);
  },

  /**
   * PATCH /treatment-groups/:id
   */
  update: async (
    id: string,
    data: UpdateTreatmentGroupRequest,
  ): Promise<TreatmentGroup> => {
    return api.patch<TreatmentGroup>(`/treatment-groups/${id}`, data);
  },

  /**
   * DELETE /treatment-groups/:id
   * El controller usa @Delete(':id'), no @Patch(':id/deactivate')
   */
  deactivate: async (id: string): Promise<TreatmentGroup> => {
    return api.delete<TreatmentGroup>(`/treatment-groups/${id}`);
  },

  /**
   * PATCH /treatment-groups/:id/reactivate
   */
  reactivate: async (id: string): Promise<TreatmentGroup> => {
    return api.patch<TreatmentGroup>(`/treatment-groups/${id}/reactivate`, {});
  },
};

export default treatmentGroupsApi;
